/**
 * @module creativeWorksSearchData
 * Composes catalog copyright API responses into app-ready search cards and detail models.
 * Fetches via `api/creativeWorksSearch`, filters and maps via sibling utils, for use in hooks.
 */

import {
  buildCreativeWorksCatalogSearchByIdUrl,
  parseCatalogSearchResultsFromResponse,
  searchCreativeWorksCatalogRaw,
} from 'api/creativeWorksSearch/creativeWorksSearchApi';
import { creativeWorksCatalogRequests } from 'api/creativeWorksSearch/creativeWorksSearchClient';

import {
  buildCatalogItemDetailUrl,
  extractItemIdFromSearchRow,
  extractItemIdFromString,
  isSafeCreativeWorksSearchItemId,
  mapLocItemResponseToDetail,
  mapCatalogResultsToCreativeWorksCards,
} from './creativeWorksSearchMappers';

import type {
  CreativeWorksSearchItem,
  CreativeWorksSearchParams,
  CreativeWorksSearchResponse,
} from './interfaces';

const hasCreativeWorksSearchNextPage = (
  pagination: Awaited<
    ReturnType<typeof searchCreativeWorksCatalogRaw>
  >['pagination']
): boolean => {
  if (pagination?.next) {
    return true;
  }
  const current = pagination?.current;
  const pages = pagination?.pages;
  return (
    typeof current === 'number' && typeof pages === 'number' && current < pages
  );
};

const hasCreativeWorksSearchPreviousPage = (
  pagination: Awaited<
    ReturnType<typeof searchCreativeWorksCatalogRaw>
  >['pagination']
): boolean => {
  if (pagination?.previous) {
    return true;
  }
  const current = pagination?.current;
  return typeof current === 'number' && current > 1;
};

/**
 * Runs a Creative Works search end-to-end for the results grid.
 * @param params - Search query, optional catalog `original-format` facet, and page (`sp`).
 * @returns One page of mapped search cards; empty results when query is blank.
 */
export const searchCreativeWorksCatalog = async (
  params: CreativeWorksSearchParams = {}
): Promise<CreativeWorksSearchResponse> => {
  const trimmedQuery = params.q?.trim();
  if (!trimmedQuery) {
    return { results: [], hasNextPage: false, hasPreviousPage: false };
  }

  const page = Math.max(1, params.page ?? 1);
  const { results, pagination } = await searchCreativeWorksCatalogRaw(
    trimmedQuery,
    params.original_format,
    page
  );

  return {
    results: mapCatalogResultsToCreativeWorksCards(results),
    hasNextPage: hasCreativeWorksSearchNextPage(pagination),
    hasPreviousPage: hasCreativeWorksSearchPreviousPage(pagination),
  };
};

/**
 * Resolves a single copyright record by catalog id, item URL, or LCCN.
 * @param rawId - Route param or user-provided identifier (may be URL-encoded).
 * @returns Full detail model, minimal fallback from search hit, or `null` when not found.
 */
export const getCreativeWorksSearchItem = async (
  rawId: string
): Promise<CreativeWorksSearchItem | null> => {
  const trimmedRawId = rawId.trim();
  let requestedId: string;
  try {
    requestedId = decodeURIComponent(trimmedRawId);
  } catch {
    requestedId = trimmedRawId;
  }
  if (!requestedId) {
    return null;
  }

  const catalogItemId =
    isSafeCreativeWorksSearchItemId(requestedId) && !requestedId.includes('://')
      ? requestedId
      : extractItemIdFromString(requestedId);

  if (catalogItemId && isSafeCreativeWorksSearchItemId(catalogItemId)) {
    const itemUrl = buildCatalogItemDetailUrl(catalogItemId);
    const itemData = await creativeWorksCatalogRequests.fetchCatalogItem(
      itemUrl
    );
    const mapped = mapLocItemResponseToDetail(itemData, requestedId);
    if (mapped) {
      return mapped;
    }
  }

  const searchUrl = buildCreativeWorksCatalogSearchByIdUrl(requestedId);
  const searchData = await creativeWorksCatalogRequests.fetchCatalogSearch(
    searchUrl
  );
  const results = parseCatalogSearchResultsFromResponse(searchData);
  const exactMatch = results.find((result) => {
    const resultId = String(result.id || '').trim();
    const resultUrl = String(result.url || '').trim();
    return resultId === requestedId || resultUrl === requestedId;
  });

  if (!exactMatch) {
    return null;
  }

  const catalogIdFromSearch = extractItemIdFromSearchRow(exactMatch);
  if (
    catalogIdFromSearch &&
    isSafeCreativeWorksSearchItemId(catalogIdFromSearch)
  ) {
    const itemUrl = buildCatalogItemDetailUrl(catalogIdFromSearch);
    const itemData = await creativeWorksCatalogRequests.fetchCatalogItem(
      itemUrl
    );
    const mapped = mapLocItemResponseToDetail(itemData, requestedId);
    if (mapped) {
      return mapped;
    }
  }

  const card = mapCatalogResultsToCreativeWorksCards([exactMatch])[0];
  if (!card) {
    return null;
  }

  const resourceUrl = card.catalog_url || card.link;
  const mimeType = Array.isArray(exactMatch.mime_type)
    ? String(exactMatch.mime_type[0] || '')
    : String(exactMatch.mime_type || '');
  const resources = resourceUrl
    ? [
        {
          url: resourceUrl,
          pdf: mimeType.toLowerCase().includes('pdf') ? resourceUrl : undefined,
          mimetype: mimeType || undefined,
        },
      ]
    : [];

  return {
    id: requestedId,
    catalog_url: resourceUrl,
    title: card.title,
    format: card.format,
    image_url: card.image_url,
    description: card.description,
    date: card.date,
    contributors: card.contributors,
    notes: Array.isArray(exactMatch.description)
      ? exactMatch.description.map((line) => String(line))
      : undefined,
    digitized: Boolean(exactMatch.digitized),
    access_restricted: Boolean(exactMatch.access_restricted),
    resources,
    digital_content: { resources },
  };
};
